import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import LangToggle, { useSiteLang } from '../components/LangToggle';
import { formatRange, formatMonth, initials } from '../utils/cvFormat';
import ShareButton from '../components/ShareButton';

// Textes de l'interface du CV en français et en anglais
const UI = {
  fr: {
    about: 'Infos', experience: 'Expérience', education: 'Formation', certifications: 'Licences et certifications',
    contactInfo: 'Coordonnées', contactMe: 'me contacter', email: 'E-mail', phone: 'Téléphone', website: 'Site web',
    skills: 'Compétences', issued: 'Délivrée en', expires: 'Expire en', credentialId: 'ID de la certification',
    showCredential: 'Voir la certification', seeMore: '…voir plus', seeLess: 'voir moins',
    soon: 'Le CV sera bientôt en ligne ✦', loading: 'chargement...', error: 'impossible de charger le CV pour le moment.',
    photo: 'Photo de profil', langLabel: 'Langue du CV',
  },
  en: {
    about: 'About', experience: 'Experience', education: 'Education', certifications: 'Licenses & certifications',
    contactInfo: 'Contact info', contactMe: 'Contact me', email: 'Email', phone: 'Phone', website: 'Website',
    skills: 'Skills', issued: 'Issued', expires: 'Expires', credentialId: 'Credential ID',
    showCredential: 'Show credential', seeMore: '…see more', seeLess: 'see less',
    soon: 'The resume will be online soon ✦', loading: 'loading...', error: 'The resume could not be loaded right now.',
    photo: 'Profile photo', langLabel: 'Resume language',
  },
};

// Types de contrat et modes de travail (saisis en français dans l'admin) → anglais façon LinkedIn
const EN_TERMS = {
  'CDI': 'Full-time', 'CDD': 'Fixed-term contract', 'Stage': 'Internship', 'Alternance': 'Apprenticeship',
  'Freelance': 'Freelance', 'Indépendant': 'Self-employed', 'Intérim': 'Temporary', 'Temps partiel': 'Part-time',
  'Bénévolat': 'Volunteer', 'Sur site': 'On-site', 'Hybride': 'Hybrid', 'À distance': 'Remote',
};

// Texte long replié sur quelques lignes, avec « …voir plus » (comme LinkedIn)
function Clamp({ text, lines = 3, t }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollHeight > el.clientHeight + 2);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [text]);
  if (!text) return null;
  return (
    <div className="cv-clamp-wrap">
      <p ref={ref} className={`cv-text ${open ? '' : 'clamped'}`} style={{ '--lines': lines }}>{text}</p>
      {(overflows || open) && (
        <button type="button" className="cv-more" onClick={() => setOpen(o => !o)}>{open ? t.seeLess : t.seeMore}</button>
      )}
    </div>
  );
}

function Logo({ src, name }) {
  return src
    ? <img className="cv-logo" src={src} alt="" />
    : <div className="cv-logo cv-logo-empty" aria-hidden="true">{initials(name)}</div>;
}

function Section({ title, children }) {
  return (
    <section className="cv-card">
      <h2 className="cv-card-title">{title}</h2>
      {children}
    </section>
  );
}

export default function CV() {
  const [lang, switchLang] = useSiteLang();
  const [cv, setCv] = useState(null);
  const [error, setError] = useState(false);
  const [showContact, setShowContact] = useState(false);

  useEffect(() => {
    fetch('/api/cv')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setCv)
      .catch(() => setError(true));
  }, []);

  const t = UI[lang];
  const en = lang === 'en';
  // Texte dans la langue choisie, ou en français si la traduction n'a pas été saisie
  const tr = (obj, key) => (en && obj[`${key}_en`] ? obj[`${key}_en`] : obj[key]) || '';
  const term = (v) => (en && v ? (EN_TERMS[v] || v) : v);

  const LangSwitch = (
    <>
      <LangToggle lang={lang} onChange={switchLang} label={t.langLabel} />
      <ShareButton lang={lang} variant="icon" title={en ? 'Resume — Fatima-Zahra Moussaid' : 'CV — Fatima-Zahra Moussaid'}
        text={en ? "Fatima-Zahra Moussaid's resume" : 'Le CV de Fatima-Zahra Moussaid'} />
    </>
  );

  if (error) return <div className="cv-page"><div className="cv-lang-row">{LangSwitch}</div><div className="empty-state">{t.error}</div></div>;
  if (!cv) return <div className="cv-page"><div className="empty-state">{t.loading}</div></div>;

  const p = cv.profile || {};
  const exp = cv.experiences || [], edu = cv.education || [], certs = cv.certifications || [];
  const about = tr(p, 'about'), headline = tr(p, 'headline'), location = tr(p, 'location');
  const isEmpty = !p.name && !about && !exp.length && !edu.length && !certs.length;
  const contacts = [
    p.email && { icon: '✉', label: t.email, value: p.email, href: `mailto:${p.email}` },
    p.phone && { icon: '☏', label: t.phone, value: p.phone, href: `tel:${p.phone.replace(/\s/g, '')}` },
    p.linkedin && { icon: 'in', label: 'LinkedIn', value: p.linkedin.replace(/^https?:\/\/(www\.)?/, ''), href: p.linkedin },
    p.website && { icon: '↗', label: t.website, value: p.website.replace(/^https?:\/\/(www\.)?/, ''), href: p.website },
  ].filter(Boolean);

  return (
    <div className="cv-page fade-in" lang={lang}>
      <div className="cv-lang-row">{LangSwitch}</div>

      {/* ---------- Carte profil ---------- */}
      <section className="cv-card cv-top">
        <div className="cv-banner" aria-hidden="true" />
        <div className="cv-top-body">
          {p.photo
            ? <img className="cv-photo" src={p.photo} alt={p.name || t.photo} />
            : <div className="cv-photo cv-photo-empty" aria-hidden="true">{initials(p.name || 'F Z')}</div>}
          <h1 className="cv-name">{p.name || 'Fatima Zahra Moussaid'}</h1>
          {headline && <p className="cv-headline">{headline}</p>}
          <p className="cv-meta">
            {location && <span>{location}</span>}
            {contacts.length > 0 && (
              <button type="button" className="cv-contact-toggle" onClick={() => setShowContact(s => !s)} aria-expanded={showContact}>
                {t.contactInfo}
              </button>
            )}
          </p>
          {showContact && (
            <ul className="cv-contacts">
              {contacts.map(c => (
                <li key={c.label}>
                  <span className="cv-contact-icon" aria-hidden="true">{c.icon}</span>
                  <span>
                    <small>{c.label}</small>
                    <a href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{c.value}</a>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="cv-top-actions">
            {p.email && <a className="btn" href={`mailto:${p.email}`}>{t.contactMe}</a>}
            {p.linkedin && <a className="btn secondary" href={p.linkedin} target="_blank" rel="noreferrer">LinkedIn ↗</a>}
          </div>
          {(exp[0] || edu[0]) && (
            <ul className="cv-top-highlights">
              {exp[0] && <li><Logo src={exp[0].logo} name={exp[0].company} /><span>{exp[0].company}</span></li>}
              {edu[0] && <li><Logo src={edu[0].logo} name={edu[0].school} /><span>{edu[0].school}</span></li>}
            </ul>
          )}
        </div>
      </section>

      {isEmpty && <div className="empty-state">{t.soon}</div>}

      {/* ---------- Infos ---------- */}
      {about && (
        <Section title={t.about}>
          <Clamp key={lang} text={about} lines={4} t={t} />
        </Section>
      )}

      {/* ---------- Expérience ---------- */}
      {exp.length > 0 && (
        <Section title={t.experience}>
          <ul className="cv-list">
            {exp.map(e => {
              const skills = tr(e, 'skills');
              const type = term(e.employment_type);
              return (
                <li key={e.id} className="cv-entry">
                  <Logo src={e.logo} name={e.company} />
                  <div className="cv-entry-body">
                    <h3>{tr(e, 'title')}</h3>
                    <p className="cv-entry-sub">{[e.company, type !== e.company && type].filter(Boolean).join(' · ')}</p>
                    <p className="cv-entry-muted">{formatRange(e.start, e.end, e.current, true, lang)}</p>
                    {(e.location || e.work_mode) && <p className="cv-entry-muted">{[tr(e, 'location'), term(e.work_mode)].filter(Boolean).join(' · ')}</p>}
                    <Clamp key={lang} text={tr(e, 'description')} t={t} />
                    {skills && (
                      <p className="cv-skills"><strong>{t.skills}{en ? ':' : ' :'}</strong> {skills.split(',').map(x => x.trim()).filter(Boolean).join(' · ')}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {/* ---------- Formation ---------- */}
      {edu.length > 0 && (
        <Section title={t.education}>
          <ul className="cv-list">
            {edu.map(e => {
              const degree = tr(e, 'degree'), field = tr(e, 'field'), grade = tr(e, 'grade');
              return (
                <li key={e.id} className="cv-entry">
                  <Logo src={e.logo} name={e.school} />
                  <div className="cv-entry-body">
                    <h3>{e.school}</h3>
                    {(degree || field) && <p className="cv-entry-sub">{[degree, field].filter(Boolean).join(', ')}</p>}
                    {(e.start || e.end) && <p className="cv-entry-muted">{formatRange(e.start, e.end, false, false, lang)}</p>}
                    {grade && <p className="cv-entry-muted">{grade}</p>}
                    <Clamp key={lang} text={tr(e, 'description')} t={t} />
                  </div>
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {/* ---------- Licences et certifications ---------- */}
      {certs.length > 0 && (
        <Section title={t.certifications}>
          <ul className="cv-list">
            {certs.map(c => (
              <li key={c.id} className="cv-entry">
                <Logo src={c.logo} name={c.issuer} />
                <div className="cv-entry-body">
                  <h3>{tr(c, 'name')}</h3>
                  <p className="cv-entry-sub">{c.issuer}</p>
                  {c.issue_date && (
                    <p className="cv-entry-muted">
                      {t.issued} {formatMonth(c.issue_date, lang)}{c.expiry_date ? ` · ${t.expires} ${formatMonth(c.expiry_date, lang)}` : ''}
                    </p>
                  )}
                  {c.credential_id && <p className="cv-entry-muted">{t.credentialId}{en ? ': ' : ' : '}{c.credential_id}</p>}
                  {c.url && <a className="cv-credential" href={c.url} target="_blank" rel="noreferrer">{t.showCredential} ↗</a>}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}
